import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, FileText, X, CheckCircle, AlertCircle } from 'lucide-react';
import ResponsiveTooltip from './ui/ResponsiveTooltip';
import { showToast } from '../utils/toast';
import { actionPointAPI } from '../Services/apiService';
import {
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MB,
  ALLOWED_ACTION_POINT_TYPES,
  ALLOWED_ACTION_POINT_EXTENSIONS,
  validateFile
} from '../constants/fileUpload';

const FileUploadModal = ({ 
  open, 
  onClose, 
  task, 
  onUploadSuccess 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Use centralized constants
  const allowedTypes = ALLOWED_ACTION_POINT_TYPES;
  const allowedExtensions = ALLOWED_ACTION_POINT_EXTENSIONS;

  const getFileTypeIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('excel') || fileType.includes('sheet')) return '📊';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📽️';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('image')) return '🖼️';
    return '📁';
  };

  const handleFileValidation = (file) => {
    const validation = validateFile(file, allowedTypes);
    if (!validation.isValid) {
      showToast.error(validation.message);
      return false;
    }
    return true;
  };

  const handleFileSelect = (file) => {
    if (handleFileValidation(file)) {
      setSelectedFile(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !task) return;

    setIsUploading(true);
    try {
      await actionPointAPI.uploadDocument(task.id, selectedFile);
      // Success toast will be shown by parent component
      onUploadSuccess();
      handleClose();
    } catch (error) {
      showToast.error('Failed to upload document');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setDragActive(false);
    onClose();
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] w-[95vw] h-[95vh] overflow-y-auto sm:h-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload Document
          </DialogTitle>
          <DialogDescription>
            Upload supporting document for: {task?.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* File Upload Area */}
          <div className="space-y-2">
            <Label>Select File</Label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive 
                  ? 'border-yellow-500 bg-yellow-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div className="space-y-3 ">
                  <div className="flex items-center gap-2 ">
                    <span className="text-2xl">{getFileTypeIcon(selectedFile.type)}</span>
                    <div className="flex-1 min-w-0 text-left">
                      <ResponsiveTooltip
                        content={<p className="text-sm whitespace-pre-wrap break-words">{selectedFile.name}</p>}
                        className="p-2 sm:max-w-md max-w-[85vw]"
                      >
                        <p className="font-medium text-sm truncate max-w-[45vw] sm:max-w-[300px]">{selectedFile.name}</p>
                      </ResponsiveTooltip>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeFile}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">File ready for upload</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                  <div>
                    <p className="text-sm font-medium">Drop file here or click to browse</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Supported: PDF, Excel, PowerPoint, Word, Images (JPG, PNG)
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Max size: {MAX_FILE_SIZE_MB}MB</p>
                  </div>
                  <Input
                    type="file"
                    onChange={handleFileInputChange}
                    accept={allowedExtensions.join(',')}
                    className="hidden"
                    id="file-upload"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Choose File
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* File Requirements */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Supported File Types:</p>
                <ul className="text-xs space-y-1">
                  <li>• Documents: PDF, DOC, DOCX</li>
                  <li>• Spreadsheets: XLS, XLSX</li>
                  <li>• Presentations: PPT, PPTX</li>
                  <li>• Images: JPG, JPEG, PNG</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            className="bg-yellow-600 hover:bg-yellow-700"
          >
            {isUploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FileUploadModal;
